import { useEffect, useRef, useState } from 'react';
import { Check, Globe, Palette } from 'lucide-react';
import {
  LANGUAGES,
  THEMES,
  usePrefsStore,
  type AppLanguage,
  type ThemeId,
} from '@/lib/prefs/usePrefsStore';
import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';

/**
 * Compact theme + language switchers for the TopBar.
 *
 * The selection is persisted via `usePrefsStore` (localStorage) so it
 * survives logout/login on the device. <ThemeApplier /> mirrors the values
 * onto <html> so CSS variable overrides re-style the entire UI.
 */

export function ThemeMenu() {
  const t = useT();
  const theme = usePrefsStore((s) => s.theme);
  const setTheme = usePrefsStore((s) => s.setTheme);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, () => setOpen(false));

  const current = THEMES.find((th) => th.id === theme) ?? THEMES[0];
  const brand = THEMES.filter((th) => th.mode === 'brand');
  const dark = THEMES.filter((th) => th.mode === 'dark');
  const light = THEMES.filter((th) => th.mode === 'light');

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 items-center gap-1.5 rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-2 text-[11px] font-bold text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-xk-border-strong)]"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${t('topbar.theme')} · ${current.label}`}
      >
        <Palette className="h-3.5 w-3.5 text-[var(--color-xk-text-muted)]" />
        <span
          aria-hidden="true"
          className="h-2.5 w-2.5 rounded-full border border-[var(--color-xk-border-strong)]"
          style={{ backgroundColor: current.preview.accent }}
        />
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-9 z-40 w-64 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-3 shadow-[0_30px_60px_-20px_rgb(0_0_0/0.6)]"
        >
          <ThemeGroup
            label={t('theme.brand')}
            items={brand}
            active={theme}
            onPick={(id) => {
              setTheme(id);
              setOpen(false);
            }}
          />
          <div className="mt-3 border-t border-[var(--color-xk-border)] pt-3">
            <ThemeGroup
              label={t('theme.dark')}
              items={dark}
              active={theme}
              onPick={(id) => {
                setTheme(id);
                setOpen(false);
              }}
            />
          </div>
          <div className="mt-3 border-t border-[var(--color-xk-border)] pt-3">
            <ThemeGroup
              label={t('theme.light')}
              items={light}
              active={theme}
              onPick={(id) => {
                setTheme(id);
                setOpen(false);
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ThemeGroup({
  label,
  items,
  active,
  onPick,
}: {
  label: string;
  items: typeof THEMES;
  active: ThemeId;
  onPick: (id: ThemeId) => void;
}) {
  return (
    <div>
      <div className="mb-2 text-[9px] font-extrabold uppercase tracking-widest text-[var(--color-xk-text-subtle)]">
        {label}
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {items.map((t) => (
          <ThemeRow
            key={t.id}
            theme={t}
            isActive={t.id === active}
            onClick={() => onPick(t.id)}
          />
        ))}
      </div>
    </div>
  );
}

function ThemeRow({
  theme,
  isActive,
  onClick,
}: {
  theme: (typeof THEMES)[number];
  isActive: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={isActive}
      onClick={onClick}
      className={cn(
        'flex items-center gap-2.5 rounded-md border px-2 py-1.5 text-left transition-colors',
        isActive
          ? 'border-[var(--color-accent-ink)] bg-[var(--color-accent-08)]'
          : 'border-[var(--color-xk-border)] hover:border-[var(--color-xk-border-strong)] hover:bg-[var(--color-xk-surface)]',
      )}
    >
      <span
        aria-hidden="true"
        className="flex h-7 w-12 flex-none overflow-hidden rounded border border-[var(--color-xk-border-strong)]"
      >
        <span className="flex-1" style={{ backgroundColor: theme.preview.bg }} />
        <span className="flex-1" style={{ backgroundColor: theme.preview.surface }} />
        <span className="w-3 flex-none" style={{ backgroundColor: theme.preview.accent }} />
      </span>
      <span className="flex-1 text-[12px] font-bold text-[var(--color-xk-lavender)]">
        {theme.label}
      </span>
      {isActive && <Check className="h-3.5 w-3.5 flex-none text-[var(--color-accent-ink)]" />}
    </button>
  );
}

export function LanguageMenu() {
  const t = useT();
  const language = usePrefsStore((s) => s.language);
  const setLanguage = usePrefsStore((s) => s.setLanguage);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  useClickOutside(wrapperRef, () => setOpen(false));
  const current = LANGUAGES.find((l) => l.id === language) ?? LANGUAGES[0];

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex h-7 items-center gap-1.5 rounded-md border border-[var(--color-xk-border)] bg-[var(--color-xk-surface)] px-2 text-[11px] font-bold text-[var(--color-xk-lavender)] transition-colors hover:border-[var(--color-xk-border-strong)]"
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${t('topbar.language')} · ${current.label}`}
      >
        <Globe className="h-3.5 w-3.5 text-[var(--color-xk-text-muted)]" />
        <span className="font-extrabold tracking-wider">{current.flag}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-9 z-40 w-40 rounded-xl border border-[var(--color-xk-border)] bg-[var(--color-xk-bg-deep)] p-1.5 shadow-[0_30px_60px_-20px_rgb(0_0_0/0.6)]"
        >
          {LANGUAGES.map((l) => {
            const isActive = l.id === language;
            return (
              <button
                key={l.id}
                type="button"
                role="menuitemradio"
                aria-checked={isActive}
                onClick={() => {
                  setLanguage(l.id as AppLanguage);
                  setOpen(false);
                }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-[12px] font-bold transition-colors',
                  isActive
                    ? 'bg-[var(--color-accent-08)] text-[var(--color-accent-ink)]'
                    : 'text-[var(--color-xk-lavender)] hover:bg-[var(--color-xk-surface)]',
                )}
              >
                <span className="w-7 text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-xk-text-muted)]">
                  {l.flag}
                </span>
                <span className="flex-1">{l.label}</span>
                {isActive && <Check className="h-3.5 w-3.5 flex-none text-[var(--color-accent-ink)]" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  onAway: () => void,
) {
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) onAway();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onAway();
    }
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [ref, onAway]);
}
